import { useState, useEffect, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ChevronDown, ChevronRight, Clock, Flag, Loader2, Lock, BookOpen, GraduationCap, FileText } from "lucide-react";
import StudentDetails from "@/components/StudentDetails";
import TheoryStudy from "@/components/theory/TheoryStudy";
import SignaturePad from "@/components/SignaturePad";
import ProgressLedger from "@/components/ProgressLedger";
import PlanningMatrix from "@/components/PlanningMatrix";
import TraineeCertificates from "@/components/TraineeCertificates";
import { Grid3x3 } from "lucide-react";

// MOS grading scale — lower is more competent. "S" = solo standard (best).
const GRADE_OPTIONS = ["S", "1", "2", "3"];
const GRADE_RANK = { S: 0, "1": 1, "2": 2, "3": 3 };
const gradeMet = (achieved, required) => {
  if (!achieved) return false;
  const a = GRADE_RANK[achieved], r = GRADE_RANK[String(required).trim()] ?? 3;
  return a != null && a <= r;
};

const STATUS = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  competent: { label: "Competent", cls: "bg-lime-100 text-lime-700 border-lime-200" },
  needs_work: { label: "Needs work", cls: "bg-amber-100 text-amber-700 border-amber-200" },
};
const THEORY_STATUS = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  passed: { label: "Passed", cls: "bg-lime-100 text-lime-700 border-lime-200" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 border-red-200" },
};
const LEVEL_CLS = {
  RPL: "bg-lime-100 text-lime-700 border-lime-200",
  PPL: "bg-sky-100 text-sky-700 border-sky-200",
  CPL: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Endorsement: "bg-purple-100 text-purple-700 border-purple-200",
};

function TheoryRow({ trainee, unit, record, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rec, setRec] = useState(() => ({
    status: record?.status || "not_started",
    exam_score: record?.exam_score ?? "",
    completed_date: record?.completed_date || "",
    notes: record?.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/training/trainees/${trainee.id}/theory/${unit.id}`, {
        ...rec, exam_score: rec.exam_score === "" ? null : +rec.exam_score,
      });
      toast.success("Theory saved"); onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const st = THEORY_STATUS[rec.status] || THEORY_STATUS.not_started;
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`theory-unit-${unit.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`theory-toggle-${unit.id}`}>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
        <span className="font-medium text-slate-800 flex-1 min-w-0 truncate">{unit.title}</span>
        {unit.level && <Badge className={`border text-[10px] ${LEVEL_CLS[unit.level] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{unit.level}</Badge>}
        {unit.prerequisite && <Badge className="border bg-orange-100 text-orange-700 border-orange-200 gap-1 text-[10px]"><Lock className="h-3 w-3" /> Prereq</Badge>}
        <Badge className={`border ${st.cls}`}>{st.label}</Badge>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-4">
          {unit.topics?.length > 0 && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Course material</Label>
              <ul className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1">
                {unit.topics.map((t) => <li key={t} className="flex items-start gap-1.5 text-sm text-slate-600"><BookOpen className="h-3.5 w-3.5 text-slate-300 mt-0.5 shrink-0" /> {t}</li>)}
              </ul>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Exam status</Label>
              <Select value={rec.status} onValueChange={(v) => setRec((r) => ({ ...r, status: v }))}>
                <SelectTrigger className="mt-1.5" data-testid={`theory-status-${unit.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(THEORY_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Exam score % <span className="text-slate-400 normal-case">(pass {unit.exam_pass_mark})</span></Label>
              <Input type="number" min="0" max="100" value={rec.exam_score} onChange={(e) => setRec((r) => ({ ...r, exam_score: e.target.value }))} className="mt-1.5" data-testid={`theory-score-${unit.id}`} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Completed date</Label>
              <Input type="date" value={rec.completed_date || ""} onChange={(e) => setRec((r) => ({ ...r, completed_date: e.target.value }))} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={rec.notes} onChange={(e) => setRec((r) => ({ ...r, notes: e.target.value }))} className="mt-1.5" rows={2} />
          </div>
          <Button onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid={`save-theory-${unit.id}`}>
            {saving ? "Saving…" : "Save theory"}
          </Button>
        </div>
      )}
    </div>
  );
}

function LessonRow({ trainee, lesson, record, locked, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rec, setRec] = useState(() => ({
    status: record?.status || "not_started",
    criteria_done: record?.criteria_done || [],
    achieved: record?.achieved || {},
    flight_time: record?.flight_time || 0,
    instructor: record?.instructor || "",
    comments: record?.comments || "",
    signed_off_date: record?.signed_off_date || "",
    student_signature: record?.student_signature || "",
    instructor_signature: record?.instructor_signature || "",
  }));
  const [saving, setSaving] = useState(false);

  const toggleCrit = (i) => setRec((r) => ({
    ...r,
    criteria_done: r.criteria_done.includes(i) ? r.criteria_done.filter((x) => x !== i) : [...r.criteria_done, i],
  }));
  const setGrade = (cid, v) => setRec((r) => ({ ...r, achieved: { ...r.achieved, [cid]: v } }));

  const elements = lesson.assessment_elements || [];
  const allCriteria = elements.flatMap((e) => e.criteria || []);
  const metCount = allCriteria.filter((c) => gradeMet(rec.achieved[c.id], c.required)).length;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/training/trainees/${trainee.id}/records/${lesson.id}`, { ...rec, flight_time: +rec.flight_time || 0 });
      toast.success("Saved"); onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const st = STATUS[rec.status] || STATUS.not_started;
  return (
    <div className={`border rounded-lg ${locked ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`} data-testid={`lesson-${lesson.id}`}>
      <button onClick={() => !locked && setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`lesson-toggle-${lesson.id}`}>
        {locked ? <Lock className="h-4 w-4 text-slate-400 shrink-0" /> : open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
        <span className="font-medium text-slate-800 flex-1 min-w-0 truncate">{lesson.title}</span>
        {lesson.milestone && <Flag className="h-3.5 w-3.5 text-orange-500 shrink-0" title="Milestone" />}
        {(lesson.dual_time > 0 || lesson.solo_time > 0) && (
          <span className="text-xs text-slate-400 hidden sm:inline">{lesson.dual_time > 0 && `${lesson.dual_time}h dual`}{lesson.dual_time > 0 && lesson.solo_time > 0 && " · "}{lesson.solo_time > 0 && `${lesson.solo_time}h solo`}</span>
        )}
        <Badge className={`border ${st.cls}`}>{st.label}</Badge>
      </button>
      {locked && (
        <p className="px-4 pb-3 -mt-1 text-xs text-orange-600 flex items-center gap-1.5"><Lock className="h-3 w-3" />
          {lesson.theory_lesson_id
            ? `Locked — pass the theory lesson “${lesson.theory_lesson_title || "prerequisite"}” to unlock this flight lesson.`
            : "Locked — pass the prerequisite theory exam to unlock this flight phase."}</p>
      )}
      {open && !locked && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-4">
          {lesson.criteria?.length > 0 && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Competency criteria</Label>
              <div className="mt-2 space-y-2">
                {lesson.criteria.map((c, i) => (
                  <label key={c} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`criterion-${lesson.id}-${i}`}>
                    <Checkbox checked={rec.criteria_done.includes(i)} onCheckedChange={() => toggleCrit(i)} className="mt-0.5" />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {elements.length > 0 && (
            <div data-testid={`assessment-grid-${lesson.id}`}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">MOS assessment</Label>
                <span className="text-xs font-semibold text-slate-600" data-testid={`assessment-met-${lesson.id}`}>{metCount}/{allCriteria.length} met</span>
              </div>
              <p className="text-[11px] text-slate-400 mb-3">Grade lower = more competent. S = solo standard, 1 = competent, 3 = trained (not consistent). Met when achieved ≤ required.</p>
              <div className="space-y-3">
                {elements.map((el) => (
                  <div key={el.id} className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 flex items-center gap-2">
                      {el.mos_ref && <Badge className="border bg-slate-200 text-slate-600 border-slate-300 text-[10px] font-mono">{el.mos_ref}</Badge>}
                      <span className="text-sm font-medium text-slate-700">{el.title}</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {(el.criteria || []).map((c) => {
                        const met = gradeMet(rec.achieved[c.id], c.required);
                        return (
                          <div key={c.id} className="flex items-start gap-3 px-3 py-2" data-testid={`assessment-criterion-${c.id}`}>
                            <span className="flex-1 text-sm text-slate-600 min-w-0">{c.ref && <span className="text-slate-400 font-mono text-xs mr-1.5">{c.ref}</span>}{c.text}</span>
                            <span className="text-[11px] text-slate-400 whitespace-nowrap pt-1.5">req {c.required}</span>
                            <Select value={rec.achieved[c.id] || ""} onValueChange={(v) => setGrade(c.id, v)}>
                              <SelectTrigger className={`w-20 h-9 shrink-0 ${rec.achieved[c.id] ? (met ? "border-lime-400 bg-lime-50" : "border-amber-400 bg-amber-50") : ""}`} data-testid={`grade-select-${c.id}`}><SelectValue placeholder="–" /></SelectTrigger>
                              <SelectContent>{GRADE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={rec.status} onValueChange={(v) => setRec((r) => ({ ...r, status: v }))}>
                <SelectTrigger className="mt-1.5" data-testid={`status-select-${lesson.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Flight time (h)</Label>
              <Input type="number" step="0.1" value={rec.flight_time} onChange={(e) => setRec((r) => ({ ...r, flight_time: e.target.value }))} className="mt-1.5" data-testid={`flight-time-${lesson.id}`} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sign-off date</Label>
              <Input type="date" value={rec.signed_off_date || ""} onChange={(e) => setRec((r) => ({ ...r, signed_off_date: e.target.value }))} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Instructor</Label>
            <Input value={rec.instructor} onChange={(e) => setRec((r) => ({ ...r, instructor: e.target.value }))} className="mt-1.5" placeholder="Instructor name" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Instructor comments</Label>
            <Textarea value={rec.comments} onChange={(e) => setRec((r) => ({ ...r, comments: e.target.value }))} className="mt-1.5" rows={2} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <SignaturePad label="Student signature" value={rec.student_signature} onChange={(v) => setRec((r) => ({ ...r, student_signature: v }))} testid={`student-sig-${lesson.id}`} />
            <SignaturePad label="Instructor signature" value={rec.instructor_signature} onChange={(v) => setRec((r) => ({ ...r, instructor_signature: v }))} testid={`instructor-sig-${lesson.id}`} />
          </div>
          <Button onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid={`save-lesson-${lesson.id}`}>
            {saving ? "Saving…" : "Save lesson"}
          </Button>
          {elements.length > 0 && (
            <Button variant="outline" className="ml-2" data-testid={`lesson-sheet-${lesson.id}`}
              onClick={() => window.open(`${API}/certificates/trainees/${trainee.id}/lesson-sheet/${lesson.id}?auth=${encodeURIComponent(localStorage.getItem("session_token") || "")}`, "_blank")}>
              <FileText className="h-4 w-4 mr-1.5" /> Lesson sheet PDF
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ProgramBlock({ trainee, entry, records, theoryRecords, rateOptions, onSaved, defaultOpen = false }) {
  const { program, summary, theory_summary, locked_phases, locked_lessons = [] } = entry;
  const [open, setOpen] = useState(defaultOpen);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const hasMatrix = (program.lessons || []).some((l) => (l.assessment_elements || []).length > 0);
  const phases = (program.phases || []).slice().sort((a, b) => a.seq - b.seq);
  const theoryUnits = (program.theory_units || []).slice().sort((a, b) => a.seq - b.seq);
  const lessonsByPhase = (pid) => (program.lessons || []).filter((l) => l.phase_id === pid).sort((a, b) => a.seq - b.seq);

  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden" data-testid={`program-block-${program.id}`}>
      {/* Compressed, clickable course header */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`program-toggle-${program.id}`}
        aria-expanded={open}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50 transition-colors"
      >
        {open ? <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />}
        <GraduationCap className="h-5 w-5 text-lime-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <h2 className="font-heading font-bold text-lg text-slate-900 truncate">{program.name}</h2>
          <div className="flex items-center gap-4 text-xs mt-1">
            <span className="text-slate-500">Flight <span className="font-semibold text-slate-800" data-testid={`prog-flight-pct-${program.id}`}>{summary.percent_complete}%</span> ({summary.competent}/{summary.total_lessons})</span>
            {theory_summary.total > 0 && <span className="text-slate-500">Theory <span className="font-semibold text-slate-800" data-testid={`prog-theory-pct-${program.id}`}>{theory_summary.percent}%</span> ({theory_summary.passed}/{theory_summary.total})</span>}
          </div>
          <Progress value={summary.percent_complete} className="h-1.5 bg-slate-100 mt-2" />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-5 border-t border-slate-100" data-testid={`program-details-${program.id}`}>
          <ProgressLedger program={program} summary={summary} records={records} rateOptions={rateOptions} onSaved={onSaved} />
          {hasMatrix && (
            <div className="mb-5">
              <Button variant="outline" size="sm" onClick={() => setMatrixOpen((o) => !o)} className="gap-1.5 text-slate-600" data-testid={`matrix-toggle-${program.id}`}>
                <Grid3x3 className="h-4 w-4" /> {matrixOpen ? "Hide" : "Show"} planning matrix
              </Button>
              {matrixOpen && (
                <div className="mt-3">
                  <PlanningMatrix program={program} records={records} />
                </div>
              )}
            </div>
          )}
          {theoryUnits.length > 0 && (
            <div className="mb-5" data-testid={`theory-section-${program.id}`}>
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Theory course</h3>
              <div className="space-y-2">
                {theoryUnits.map((u) => <TheoryRow key={u.id} trainee={trainee} unit={u} record={theoryRecords[u.id]} onSaved={onSaved} />)}
              </div>
            </div>
          )}

          {phases.map((ph) => {
            const locked = locked_phases.includes(ph.id);
            return (
              <div key={ph.id} className="mb-4" data-testid={`phase-${ph.id}`}>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">{ph.name} {locked && <Lock className="h-3.5 w-3.5 text-orange-500" />}</h3>
                <div className="space-y-2">
                  {lessonsByPhase(ph.id).map((l) => (
                    <LessonRow key={l.id} trainee={trainee} lesson={l} record={records[l.id]} locked={locked || locked_lessons.includes(l.id)} onSaved={onSaved} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TraineeDetail({ traineeId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rateOptions, setRateOptions] = useState({ aircraft: [], instructors: [] });

  const load = useCallback(async () => {
    try { const r = await api.get(`/training/trainees/${traineeId}/progress`); setData(r.data); }
    catch { toast.error("Failed to load"); } finally { setLoading(false); }
  }, [traineeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/training/rate-options").then((r) => setRateOptions(r.data)).catch(() => {}); }, []);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;
  if (!data) return null;

  const { trainee, programs, records, theory_records, summary } = data;

  return (
    <div className="space-y-6 fade-up" data-testid="trainee-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="trainee-back-btn"><ArrowLeft className="h-4 w-4" /> All trainees</button>

      <div className="bg-slate-900 text-white rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold font-heading">{trainee.first_name} {trainee.last_name}</h1>
            <p className="text-slate-300 text-sm mt-1">{summary.programs > 0 ? `${summary.programs} program${summary.programs > 1 ? "s" : ""} enrolled` : "No program assigned"}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400 mt-2">
              {trainee.reference_no && <span>Ref: {trainee.reference_no}</span>}
              {trainee.email && <span>{trainee.email}</span>}
              {trainee.medical_expiry && <span>Medical exp: {trainee.medical_expiry}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end"><Clock className="h-4 w-4 text-lime-400" /><span className="font-heading font-bold text-lg">{summary.hours_logged}h</span></div>
            <p className="text-xs text-slate-400">logged</p>
          </div>
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-sm mb-1.5"><span className="text-slate-300">Overall progress</span><span className="font-semibold text-lime-400" data-testid="progress-percent">{summary.percent_complete}% · {summary.competent}/{summary.total_lessons} competent</span></div>
          <Progress value={summary.percent_complete} className="h-2 bg-slate-700" />
        </div>
      </div>

      <StudentDetails trainee={trainee} onSaved={load} />

      <TraineeCertificates trainee={trainee} />

      <TheoryStudy trainee={trainee} disciplines={[...new Set((programs || []).map((e) => e.program.discipline).filter(Boolean))]} onProgress={load} />

      {(!programs || programs.length === 0) ? (
        <p className="text-sm text-slate-500">Assign one or more programs to this trainee to start tracking lessons and theory.</p>
      ) : (
        <div className="space-y-5">
          {programs.map((entry) => (
            <ProgramBlock key={entry.program.id} trainee={trainee} entry={entry} records={records} theoryRecords={theory_records} rateOptions={rateOptions} onSaved={load} defaultOpen={programs.length === 1} />
          ))}
        </div>
      )}
    </div>
  );
}
